<script setup>
import { ref, computed, watch } from 'vue'
import AppLayout from '@/layouts/AppLayout.vue'

const props = defineProps({
  bons:       { type: Array,   default: () => [] },
  show_all:   { type: Boolean, default: false },
  can_create: { type: Boolean, default: false },
  total_count:  { type: Number, default: 0 },
  actifs_count: { type: Number, default: 0 },
})

const search      = ref('')
const pageSize    = ref(25)
const currentPage = ref(1)
const sortKey     = ref('numero')
const sortDir     = ref('desc')

watch([search, pageSize, sortKey, sortDir], () => { currentPage.value = 1 })

function toggleSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortDir.value = 'asc'
  }
}

const filtered = computed(() => {
  let list = [...props.bons]
  if (search.value.trim()) {
    const q = search.value.toLowerCase()
    list = list.filter(b =>
      b.numero_str?.toLowerCase().includes(q) ||
      b.projet_code?.toLowerCase().includes(q) ||
      b.nom?.toLowerCase().includes(q) ||
      b.type_bon?.toLowerCase().includes(q) ||
      b.statut?.toLowerCase().includes(q)
    )
  }
  list.sort((a, b) => {
    let av = a[sortKey.value] ?? ''
    let bv = b[sortKey.value] ?? ''
    if (av < bv) return sortDir.value === 'asc' ? -1 : 1
    if (av > bv) return sortDir.value === 'asc' ?  1 : -1
    return 0
  })
  return list
})

const totalPages = computed(() => Math.ceil(filtered.value.length / pageSize.value))

const paged = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return filtered.value.slice(start, start + pageSize.value)
})

const pageNumbers = computed(() => {
  const total = totalPages.value
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const cur = currentPage.value
  const pages = new Set([1, total, cur])
  if (cur > 1) pages.add(cur - 1)
  if (cur < total) pages.add(cur + 1)
  return [...pages].sort((a, b) => a - b)
})

const columns = [
  { key: 'numero',      label: 'Numéro',    sortable: true,  cls: 'w-28' },
  { key: 'projet_code', label: 'Projet',    sortable: true,  cls: 'w-32' },
  { key: 'type_bon',    label: 'Type',      sortable: true,  cls: 'w-36' },
  { key: 'nom',         label: 'Nom',       sortable: true,  cls: '' },
  { key: 'date',        label: 'Création',  sortable: true,  cls: 'w-32' },
  { key: 'debut',       label: 'Début',     sortable: true,  cls: 'w-32' },
  { key: 'statut',      label: 'Statut',    sortable: true,  cls: 'w-36' },
]

function fmt(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('fr-CA', { year: 'numeric', month: 'short', day: 'numeric' })
}

const statutColor = (s) => {
  if (!s) return 'bg-outline/10 text-outline border-outline/10'
  const sl = s.toLowerCase()
  if (sl.includes('cours'))  return 'bg-primary/10 text-primary border-primary/20'
  if (sl.includes('traité') || sl.includes('traite')) return 'bg-tertiary/15 text-tertiary border-tertiary/30'
  return 'bg-outline/10 text-outline border-outline/10'
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="relative w-80">
        <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline text-sm pointer-events-none">search</span>
        <input
          v-model="search"
          type="text"
          placeholder="Rechercher un bon..."
          class="w-full bg-surface-container-highest border-none rounded-md pl-10 pr-4 py-2 text-sm text-on-surface placeholder-outline focus:ring-2 focus:ring-primary/20 outline-none transition-all"
        />
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-7xl mx-auto">

        <!-- Header -->
        <div class="mb-8 flex justify-between items-end">
          <div>
            <h1 class="text-2xl font-black text-on-surface tracking-tight uppercase mb-2">Bons de travail</h1>
            <div class="flex items-center gap-4 text-[10px] font-black text-outline uppercase tracking-widest">
              <span class="flex items-center gap-1.5">
                <span class="w-2 h-2 rounded-full bg-tertiary"></span>
                {{ actifs_count }} Actifs
              </span>
              <span class="flex items-center gap-1.5">
                <span class="w-2 h-2 rounded-full bg-outline/50"></span>
                {{ total_count }} Total
              </span>
            </div>
          </div>
          <div class="flex items-center gap-3">
            <!-- Sélecteur par page -->
            <div class="flex items-center gap-2 bg-surface-container-low px-3 py-2 rounded-md border border-outline-variant/10">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline">Affichage</span>
              <select
                v-model="pageSize"
                class="bg-transparent border-none text-xs font-black text-primary p-0 focus:ring-0 cursor-pointer uppercase outline-none"
              >
                <option :value="10">10 Bons</option>
                <option :value="25">25 Bons</option>
                <option :value="50">50 Bons</option>
                <option :value="100">100 Bons</option>
              </select>
            </div>
            <a
              v-if="can_create"
              href="/bons/new"
              class="machined-sheen px-5 py-2.5 rounded-md text-on-primary font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:brightness-110 active:scale-95 transition-all shadow-lg"
            >
              <span class="material-symbols-outlined text-sm">add_circle</span>
              Nouveau bon
            </a>
          </div>
        </div>

        <!-- Onglets -->
        <div class="flex items-center gap-1 mb-6">
          <a
            href="/bons"
            :class="!show_all ? 'bg-primary text-on-primary' : 'text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10'"
            class="px-4 py-2 text-xs font-black uppercase tracking-widest rounded-sm transition-all"
          >Projets actifs</a>
          <a
            href="/bons?all=true"
            :class="show_all ? 'bg-primary text-on-primary' : 'text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10'"
            class="px-4 py-2 text-xs font-black uppercase tracking-widest rounded-sm transition-all"
          >Tous les bons</a>
        </div>

        <!-- Table -->
        <div class="bg-surface-container rounded-xl overflow-hidden shadow-2xl border border-outline-variant/10">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-surface-container-high/60 border-b border-outline-variant/15">
                <th
                  v-for="col in columns"
                  :key="col.key"
                  :class="['px-6 py-4 text-[10px] font-black uppercase tracking-widest text-outline', col.cls, col.sortable ? 'cursor-pointer select-none hover:text-on-surface transition-colors' : '']"
                  @click="col.sortable && toggleSort(col.key)"
                >
                  <span class="flex items-center gap-1">
                    {{ col.label }}
                    <span v-if="col.sortable" class="material-symbols-outlined text-[12px]">
                      {{ sortKey === col.key ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}
                    </span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/10">
              <tr
                v-for="b in paged"
                :key="b.id"
                class="hover:bg-surface-container-highest/20 transition-colors"
              >
                <td class="px-6 py-3">
                  <a :href="`/bons/${b.id}`" class="font-mono text-sm font-bold text-primary hover:brightness-125 transition-all">{{ b.numero_str }}</a>
                </td>
                <td class="px-6 py-3">
                  <a :href="`/projets/${b.projet_id}`" class="font-mono text-xs font-bold text-on-surface hover:text-primary transition-all">{{ b.projet_code }}</a>
                </td>
                <td class="px-6 py-3 text-xs text-outline">{{ b.type_bon || '—' }}</td>
                <td class="px-6 py-3 text-sm text-on-surface">{{ b.nom || '—' }}</td>
                <td class="px-6 py-3 text-xs text-outline">{{ fmt(b.date) }}</td>
                <td class="px-6 py-3 text-xs text-outline">{{ fmt(b.debut) }}</td>
                <td class="px-6 py-3">
                  <span
                    v-if="b.statut"
                    :class="statutColor(b.statut)"
                    class="inline-flex px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border"
                  >{{ b.statut }}</span>
                  <span v-else class="text-xs text-outline">—</span>
                </td>
              </tr>

              <!-- Empty state -->
              <tr v-if="paged.length === 0">
                <td colspan="7" class="px-6 py-16 text-center text-outline text-sm">
                  <span class="material-symbols-outlined text-4xl block mb-3 opacity-30">receipt_long</span>
                  Aucun bon trouvé{{ search ? ` pour "${search}"` : '' }}.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="mt-4 flex items-center justify-between gap-4">
          <p class="text-xs text-outline font-bold">
            {{ filtered.length }} résultat{{ filtered.length !== 1 ? 's' : '' }} — page {{ currentPage }} / {{ totalPages }}
          </p>
          <div class="flex items-center gap-1">
            <button
              @click="currentPage--"
              :disabled="currentPage === 1"
              class="px-3 py-2 text-xs font-black rounded-md border border-outline-variant/20 text-outline hover:bg-surface-container-highest disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            ><span class="material-symbols-outlined text-sm">chevron_left</span></button>
            <template v-for="(page, i) in pageNumbers" :key="page">
              <span v-if="i > 0 && page - pageNumbers[i-1] > 1" class="px-2 text-outline text-xs">…</span>
              <button
                @click="currentPage = page"
                :class="page === currentPage ? 'bg-primary text-on-primary shadow-md' : 'text-outline hover:bg-surface-container-highest border border-outline-variant/20'"
                class="min-w-[2rem] px-3 py-2 text-xs font-black rounded-md transition-all"
              >{{ page }}</button>
            </template>
            <button
              @click="currentPage++"
              :disabled="currentPage === totalPages"
              class="px-3 py-2 text-xs font-black rounded-md border border-outline-variant/20 text-outline hover:bg-surface-container-highest disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            ><span class="material-symbols-outlined text-sm">chevron_right</span></button>
          </div>
        </div>

        <!-- Footer -->
        <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
          Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
        </footer>

      </div>
    </div>
  </AppLayout>
</template>
